import React, { useState, useEffect } from 'react';
import { API_BASE } from '../utils/api';
import '../styles/FilmModal.css';

function formatDuree(iso) {
  if (!iso) return null;
  const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!m) return iso;
  const h = m[1] ? `${m[1]}h` : '';
  const min = m[2] ? m[2].padStart(h ? 2 : 1, '0') : '';
  return `${h}${min}`.trim() || null;
}

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatHeure(iso) {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function minutesFromISO(iso) {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

function mapsUrl(nom) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${nom}, Paris`)}`;
}

// Distance brute plutôt qu'un temps de marche: valable quel que soit le
// mode de déplacement, contrairement à une estimation en minutes.
function libelleDistance(m) {
  if (m == null) return null;
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;
}

function extraireCineId(url) {
  const m = url.match(/[?&]cine=(\d+)/);
  return m ? m[1] : null;
}

// Séances de ce film ce jour-là, limitées aux cinémas déjà retenus par le
// rayon de recherche en cours (la liste `cinemas` reçue par CinemasSection,
// avec sa distance en minutes de marche déjà calculée) — pas tous les
// cinémas de Paris.
function useSeancesDuJour(filmId, day, cinemasProches) {
  const [state, setState] = useState('loading');
  const [seances, setSeances] = useState([]);

  useEffect(() => {
    if (!filmId || !day) return;
    let cancelled = false;
    setState('loading');

    fetch(`${API_BASE}/api/cinema/cache`)
      .then(r => r.json())
      .then(({ cache }) => {
        if (cancelled) return;
        const infosParId = new Map(
          (cinemasProches || [])
            .map(c => [extraireCineId(c.url), c])
            .filter(([id]) => id)
        );
        const tout = [];
        for (const [cineId, jours] of Object.entries(cache || {})) {
          const infos = infosParId.get(cineId);
          if (!infos) continue;
          for (const s of jours[day] || []) {
            if (s.film?.id === filmId) {
              tout.push({ ...s, cinemaNom: infos.nom, distanceM: infos.distance_m });
            }
          }
        }
        tout.sort((a, b) => minutesFromISO(a.heure) - minutesFromISO(b.heure));
        setSeances(tout);
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('error'); });

    return () => { cancelled = true; };
  }, [filmId, day, cinemasProches]);

  return { state, seances };
}

// Notes multi-sources: celle de SensCritique vient de la fiche détaillée
// (scrapée à l'ouverture), les autres viennent déjà du film tel que fourni
// par paris-cine.info avec les séances (pas d'appel réseau supplémentaire).
// Les logos sont ceux utilisés par paris-cine.info lui-même (repris dans
// public/logos), pour identifier chaque source d'un coup d'œil sans texte —
// gain de place, et toutes les notes tiennent sur une ligne plus souvent.
// Metacritic n'a pas de logo dédié sur leur site: on garde le texte pour
// celui-là uniquement.
function noteBadges(film, details) {
  const badges = [];
  if (details?.note != null) {
    badges.push({ logo: '/logos/sc_r.svg', alt: 'SensCritique', valeur: `${details.note}/${details.noteSur}` });
  }
  if (film.noteImdb) {
    badges.push({ logo: '/logos/imdb_r.svg', alt: 'IMDb', valeur: `${film.noteImdb}/10` });
  }
  if (film.noteAlloCinePresse) {
    badges.push({ logo: '/logos/allop_r.png', alt: 'AlloCiné presse', valeur: `${film.noteAlloCinePresse}/5` });
  }
  if (film.noteAlloCineSpectateurs) {
    badges.push({ logo: '/logos/allos_r.png', alt: 'AlloCiné spectateurs', valeur: `${film.noteAlloCineSpectateurs}/5` });
  }
  if (film.noteRottenTomatoes) {
    badges.push({ logo: '/logos/rt_r.svg', alt: 'Rotten Tomatoes', valeur: `${film.noteRottenTomatoes}%` });
  }
  if (film.noteMetacritic) {
    badges.push({ label: 'Metacritic', valeur: `${film.noteMetacritic}/100` });
  }
  if (film.noteLetterboxd) {
    badges.push({ logo: '/logos/lbox_r.svg', alt: 'Letterboxd', valeur: `${film.noteLetterboxd}/5` });
  }
  return badges;
}

// Pop-up (superposée à la page actuelle, pas de navigation) affichant la
// fiche d'un film: affiche, résumé, acteurs, réalisateur, etc. via SensCritique,
// complétée par les notes IMDb/AlloCiné/Rotten Tomatoes/Metacritic/Letterboxd
// déjà connues de paris-cine.info.
function FilmModal({ film, day, cinemas, onClose }) {
  const scUrl = film?.scUrl;
  const [state, setState] = useState('loading');
  const [details, setDetails] = useState(null);
  const { state: seancesState, seances: seancesDuJour } = useSeancesDuJour(film?.id, day, cinemas);

  useEffect(() => {
    if (!scUrl) return;
    let cancelled = false;
    setState('loading');
    fetch(`${API_BASE}/api/film-details?sc=${encodeURIComponent(scUrl)}`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data.error) {
          setState('error');
        } else {
          setDetails(data);
          setState('ready');
        }
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [scUrl]);

  if (!film) return null;

  return (
    <div className="film-modal-backdrop" onClick={onClose}>
      <div className="film-modal" onClick={e => e.stopPropagation()}>
        <button className="film-modal-close" onClick={onClose} aria-label="Fermer">✕</button>

        {state === 'loading' && <p className="film-modal-msg">Chargement de la fiche…</p>}
        {state === 'error' && <p className="film-modal-msg">Impossible de charger la fiche du film</p>}

        {state === 'ready' && details && (
          <div className="film-modal-body">
            {details.affiche && (
              <img className="film-modal-affiche" src={details.affiche} alt={details.titre} />
            )}
            <div className="film-modal-infos">
              <h3 className="film-modal-titre">{details.titre}</h3>
              {details.titreAlternatif && details.titreAlternatif !== details.titre && (
                <p className="film-modal-titre-alt">{details.titreAlternatif}</p>
              )}

              <div className="film-modal-meta">
                {details.realisateurs?.length > 0 && (
                  <span>🎬 {details.realisateurs.join(', ')}</span>
                )}
                {formatDate(details.datePublication) && (
                  <span>📅 {formatDate(details.datePublication)}</span>
                )}
                {formatDuree(details.duree) && (
                  <span>⏱ {formatDuree(details.duree)}</span>
                )}
              </div>

              {noteBadges(film, details).length > 0 && (
                <div className="film-modal-notes">
                  {noteBadges(film, details).map((n, i) => (
                    <span key={i} className="film-modal-note-badge" title={n.alt || n.label}>
                      {n.logo
                        ? <img src={n.logo} alt={n.alt} />
                        : <strong>{n.label}</strong>}
                      {n.valeur}
                    </span>
                  ))}
                </div>
              )}

              {details.genres?.length > 0 && (
                <div className="film-modal-genres">
                  {details.genres.map((g, i) => <span key={i}>{g}</span>)}
                </div>
              )}

              {details.resume && (
                <p className="film-modal-resume">{details.resume}</p>
              )}

              {details.acteurs?.length > 0 && (
                <p className="film-modal-acteurs">
                  <strong>Avec :</strong> {details.acteurs.join(', ')}
                </p>
              )}

              {details.bandeAnnonceYoutubeId && (
                <div className="film-modal-trailer">
                  <p className="film-modal-trailer-titre">🎬 Bande-annonce</p>
                  <div className="film-modal-trailer-frame">
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${details.bandeAnnonceYoutubeId}`}
                      title="Bande-annonce"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}

              {seancesState === 'ready' && seancesDuJour.length > 0 && (
                <div className="film-modal-seances">
                  <p className="film-modal-seances-titre">
                    🎟️ {seancesDuJour.length} séance{seancesDuJour.length > 1 ? 's' : ''} ce jour
                  </p>
                  <div className="film-modal-seances-liste">
                    {seancesDuJour.map((s, i) => (
                      <div key={i} className="film-modal-seance-row">
                        {s.lien
                          ? <a href={s.lien} target="_blank" rel="noreferrer" className="film-modal-seance-heure film-modal-seance-heure-lien" title="Réserver">{formatHeure(s.heure)}</a>
                          : <span className="film-modal-seance-heure">{formatHeure(s.heure)}</span>}
                        <a href={mapsUrl(s.cinemaNom)} target="_blank" rel="noreferrer" className="film-modal-seance-cinema">
                          {s.cinemaNom}{s.distanceM != null && ` (${libelleDistance(s.distanceM)})`}
                        </a>
                        {s.salle && <span className="film-modal-seance-salle">Salle {s.salle}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FilmModal;
